import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useGetClientDiagnosis from "hooks/api/client/useGetClientDiagnosis";
import useGetListOfDiagnosis from "hooks/api/client/useGetListOfDiagnosis";
import { useUpdateClientDiagnosis } from "hooks/api/client/useUpdateClientDiagnosis";
import useGetAdditionalDiagnoses from "hooks/api/client/useGetAdditionalDiagnoses";
import useCreateAdditionalDiagnosis from "hooks/api/client/useCreateAdditionalDiagnosis";
import useUpdateAdditionalDiagnosis from "hooks/api/client/useUpdateAdditionalDiagnosis";
import useDeleteAdditionalDiagnosis from "hooks/api/client/useDeleteAdditionalDiagnosis";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import { FormikProvider, useFormik } from "formik";
import { getDiagnosticFormValues, getFormValuesParsedForAPI } from "./utils";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import RowOptions from "../../Widgets/RowOptions";
import DiagnosticModal, { DiagnosticModalSaveValues } from "./DiagnosticModal";
import { useSelector } from "react-redux";
import * as selectors from 'selectors';

const ADDITIONAL_DIAGNOSIS_COLUMNS: DataTableColumn[] = [
  { key: "additionalDiagnosis", label: "Additional Diagnosis" },
  { key: "dateOfDiagnosis", label: "Date of Diagnosis" },
  { key: "diagnosingPhysician", label: "Diagnosing Physician" },
  { key: "contactInformation", label: "Contact Information" },
  { key: "actions", label: "" },
];

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

export const DiagnosticProfile: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_info);
  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);

  const {
    clientDiagnosis,
    clientDiagnosisLoading: diagnosisLoading,
    refetchClientDiagnosis
  } = useGetClientDiagnosis(clientIdNum);

  const { listOfDiagnosis = [], listOfDiagnosisLoading: listLoading } = useGetListOfDiagnosis(clientIdNum);
  const { updateClientDiagnosisMutation: updateClientDiagnosis } = useUpdateClientDiagnosis();

  const { additionalDiagnoses, refetchAdditionalDiagnoses } = useGetAdditionalDiagnoses(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createAdditionalDiagnosisMutation, isCreateAdditionalDiagnosisLoading } = useCreateAdditionalDiagnosis({
    onSuccess: () => {
      refetchAdditionalDiagnoses();
      closeModal();
    },
  });

  const { updateAdditionalDiagnosisMutation, isUpdateAdditionalDiagnosisLoading } = useUpdateAdditionalDiagnosis({
    onSuccess: () => {
      refetchAdditionalDiagnoses();
      closeModal();
    },
  });

  const { deleteAdditionalDiagnosisMutation } = useDeleteAdditionalDiagnosis({
    onSuccess: () => {
      refetchAdditionalDiagnoses();
    },
  });

  const formik = useFormik({
    initialValues: {
      diagnosticProfile: getDiagnosticFormValues(clientDiagnosis),
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!clientId) return;
      const payload = getFormValuesParsedForAPI(values.diagnosticProfile);
      updateClientDiagnosis(
        { clientId: Number(clientId), payload },
        {
          onSuccess: async () => {
            await refetchClientDiagnosis();
            setEditMode(false);
          },
        }
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    setEditMode(false);
  };

  const handleSave = () => {
    formik.handleSubmit();
  };

  const handleModalSave = (values: DiagnosticModalSaveValues) => {
    if (!clientIdNum) return;
    const payload = {
      diagnosisName: values.additionalDiagnosis || null,
      diagnosisDate: values.dateOfDiagnosis ? values.dateOfDiagnosis.toISOString() : null,
      diagnosingPhysicianName: values.diagnosingProviderName || null,
      diagnosingPhysicianContact: values.diagnosingProviderContact || null,
    };

    if (selectedRow) {
      updateAdditionalDiagnosisMutation({ id: Number(selectedRow.id), payload });
    } else {
      createAdditionalDiagnosisMutation({ clientId: clientIdNum, payload });
    }
  };

  const rows: DataTableRow[] = additionalDiagnoses.map((item: any) => ({
    id: String(item.id),
    additionalDiagnosis: item.diagnosisName || "-",
    dateOfDiagnosis: formatDate(item.diagnosisDate),
    diagnosingPhysician: item.diagnosingPhysicianName || "-",
    contactInformation: item.diagnosingPhysicianContact || "-",
    _diagnosisDate: item.diagnosisDate || null,
    _diagnosingPhysicianName: item.diagnosingPhysicianName || "",
    _diagnosingPhysicianContact: item.diagnosingPhysicianContact || "",
  }));

  if (diagnosisLoading || listLoading) {
    return <ANLoadingScreen />;
  }

  return (
    <FormikProvider value={formik}>
      <SectionWrapper
        title="Diagnostic Profile"
        description="Essential diagnostic and treatment information used to analyze client outcomes and measure program effectiveness."
        editMode={editMode}
        onEdit={canEdit ? () => setEditMode(true) : undefined}
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={!formik.dirty}
      >
        {editMode ? (
          <EditFields listOfDiagnosis={listOfDiagnosis} />
        ) : (
          <ViewFields
            diagnosticValues={formik.values.diagnosticProfile}
            listOfDiagnosis={listOfDiagnosis}
          />
        )}

        <DataTable
          columns={ADDITIONAL_DIAGNOSIS_COLUMNS}
          rows={rows}
          ariaLabel="Additional diagnoses"
          emptyMessage="No additional diagnosis records"
          onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
          sx={{ mt: 7 }}
          renderCell={(col, row) => {
            if (col.key === "actions") {
              return (
                <RowOptions
                  onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                  onDelete={() => deleteAdditionalDiagnosisMutation(Number(row.id))}
                />
              );
            }
            return row[col.key] || "-";
          }}
        />
      </SectionWrapper>

      <DiagnosticModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        clientId={clientId}
        isSaving={isCreateAdditionalDiagnosisLoading || isUpdateAdditionalDiagnosisLoading}
        initialValues={selectedRow ? {
          additionalDiagnosis: selectedRow.additionalDiagnosis !== "-" ? selectedRow.additionalDiagnosis : "",
          dateOfDiagnosis: selectedRow._diagnosisDate ? new Date(selectedRow._diagnosisDate as string) : null,
          diagnosingProviderName: selectedRow._diagnosingPhysicianName as string,
          diagnosingProviderContact: selectedRow._diagnosingPhysicianContact as string,
        } : undefined}
      />
    </FormikProvider>
  );
};

export default DiagnosticProfile;
